import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Lock as LockIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { getStudentProgress, updateProgress } from '../../helper/progress_helper';

const FirstPart = ({ course, modules, setLessonIndex, setModuleIndex, gl, ac }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState({});

  const handleAccordionChange = (index, module) => (event, isExpanded) => {
    if (module.status === 'UNLOCK') {
      setExpanded((prev) => ({
        ...prev,
        [index]: isExpanded,
      }));
    }
  };

  const handleExpandAll = () => {
    const newExpanded = {};
    modules?.forEach((module, index) => {
      if (module.status === 'UNLOCK') {
        newExpanded[index] = true;
      }
    });
    setExpanded(newExpanded);
  };

  return (
    <Box sx={styles.container}>
      <Typography
        variant={isMobile ? 'h6' : 'h5'}
        sx={styles.title}
        gutterBottom
      >
        Course Content
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'space-between', md: 'space-between' },
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2
        }}
      >
        <Typography
          variant={isMobile ? 'body2' : 'body1'}
          color="text.secondary"
          sx={styles.subtitle}
        >
          {modules?.length || 0} modules {course?.duration} Hours
        </Typography>
        <Link
          component="button"
          variant="body2"
          onClick={handleExpandAll}
          sx={styles.expandAll}
          underline="hover"
        >
          Expand all sections
        </Link>
      </Box>
      <Box sx={styles.allModules}>
        {modules?.map((module, index) => (
          <Accordion
            key={index}
            expanded={expanded[index]}
            onChange={handleAccordionChange(index, module)}
            disabled={module.status === 'LOCK'}
            sx={styles.module}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                ...styles.header,
                '&.Mui-disabled': {
                  backgroundColor: 'grey.100',
                  cursor: 'not-allowed'
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <Box sx={styles.headerTitle}>
                  <Typography
                    component="span"
                    variant="subtitle2"
                    color="primary"
                    sx={styles.moduleNumber}
                  >
                    {index + 1}.
                  </Typography>
                  <Typography variant="h6" sx={{ ml: 1, display: 'inline' }}>
                    {module.title}
                  </Typography>
                  <Chip
                    label={module?.type}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ ml: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
                  />
                  {module.status === 'LOCK' && (
                    <LockIcon sx={styles.lockIcon} />
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={styles.lectures}>
              {module.lessons.length > 0 && (
                <List dense sx={{ py: 0 }}>
                  {module.lessons.map((lesson, i) => (
                    <ListItem
                      key={i}
                      button
                      onClick={() => {
                        setLessonIndex(i);
                        setModuleIndex(index);
                        gl(lesson);
                      }}
                      sx={{
                        py: 0.5,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {index + 1}.{i + 1} {lesson?.title} ({lesson?.duration} min)
                          </Typography>
                        }
                      />
                      {module?.progress?.lessonTrack?.[i] && (
                        <ListItemIcon sx={{ minWidth: 32, justifyContent: 'flex-end' }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
              {module.assessment.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'} sx={{ mb: 1, fontWeight: 500 }}>
                    Assessments
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {module.assessment.map((assessment, i) => (
                      <ListItem
                        key={i}
                        button
                        onClick={() => {
                          ac(assessment);
                          setModuleIndex(index);
                        }}
                        sx={{
                          py: 0.5,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {index + 1}.{i + 1} {assessment?.title}
                            </Typography>
                          }
                        />
                        {module?.progress?.assessment && (
                          <ListItemIcon sx={{ minWidth: 32, justifyContent: 'flex-end' }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default FirstPart;

const styles = {
  container: {
    fontFamily: 'Arial, sans-serif',
    px: { xs: 1, sm: 2 },
    py: 2
  },
  allModules: {
    maxHeight: { xs: '60vh', sm: '70vh', md: '80vh' },
    overflowY: 'auto'
  },
  title: {
    fontWeight: 'bold',
    mb: 1
  },
  subtitle: {
    flexGrow: 1
  },
  expandAll: {
    color: 'primary.main',
    fontSize: { xs: '0.8rem', sm: '0.875rem' },
    cursor: 'pointer',
    textAlign: 'right',
    whiteSpace: 'nowrap'
  },
  module: {
    my: 1,
    '&:before': {
      display: 'none'
    }
  },
  header: {
    backgroundColor: 'grey.50',
    px: 2,
    py: 1.5,
    '& .MuiAccordionSummary-content': {
      m: 0
    }
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1
  },
  moduleNumber: {
    fontWeight: 'bold',
    mr: 1
  },
  lockIcon: {
    ml: 1,
    color: 'error.main',
    fontSize: { xs: '1rem', sm: '1.1rem' }
  },
  lectures: {
    backgroundColor: 'grey.50',
    px: { xs: 1, sm: 2 },
    py: 1
  }
};